import { parseLatexToMathML } from './renderLatexToMathML';

const ZWSP_PATTERN = /\u200B/g;
// `countQuizText` 의 공백 판정과 같은 문자 집합(서버 Kotlin `\s` 와 맞춘 것).
const BLANK_PATTERN = /^[ \t\n\r\f\v\u00A0]*$/;
// MathLive 가 입력 중 빈칸에 넣는 명령. 화면엔 □ 로 그려지지만 아직 입력한 글자가 아니다.
// 지우지 않고 `{}` 로 바꿔야 `x^\placeholder{}` 같은 입력 중 수식이 파싱된다.
const PLACEHOLDER_PATTERN = /\\placeholder(?:\[[^\]]*\])?\{[^{}]*\}/g;
// 화면에 글자로 보이는 MathML 잎. 분수선·루트 모양 같은 구조는 잎이 아니라 세지 않는다.
const LEAF_PATTERN = /<(mi|mn|mo|mtext|ms)\b[^>]*>([^<]*)<\/\1>/g;
// Temml 이 잎 텍스트를 escape 하는 표(`temml` 의 ESCAPE_LOOKUP)의 역.
const TEMML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&gt;': '>',
  '&lt;': '<',
  '&quot;': '"',
  '&#x27;': "'",
};
const TEMML_ENTITY_PATTERN = /&(?:amp|gt|lt|quot|#x27);/g;
// 보이지 않는 연산자(함수 적용·곱셈·구분·덧셈)와 ZWSP 는 화면에 없다.
const INVISIBLE_PATTERN = /[\u2061-\u2064\u200B]/g;

// 에디터는 트랜잭션마다 문서 전체를 두 번 센다. 같은 수식을 매번 다시 파싱하지 않게 한다.
const CACHE_LIMIT = 500;
const cache = new Map<string, number>();

const countRenderedSymbols = (latex: string): number => {
  let mathML: string;
  try {
    mathML = parseLatexToMathML(latex.replace(PLACEHOLDER_PATTERN, '{}'), {
      displayMode: false,
      annotate: false,
    });
  } catch {
    // 못 읽는 수식은 화면에 원문이 그대로 나온다(`renderLatexToMathML` 폴백).
    return latex.length;
  }

  let symbols = 0;
  for (const [, , text] of mathML.matchAll(LEAF_PATTERN)) {
    const visible = text
      .replace(TEMML_ENTITY_PATTERN, entity => TEMML_ENTITIES[entity])
      .replace(INVISIBLE_PATTERN, '');
    symbols += Array.from(visible).length;
  }
  // 그려지는 글자가 없어도 서버는 공백이 아닌 수식을 1자로 센다. 그보다 적게 세면 안 된다.
  return Math.max(1, symbols);
};

/**
 * 칸 글자 수에서 수식 하나가 차지하는 글자 수(`countVisibleQuizText`). 화면에 보이는 글자를
 * 하나씩 센다(`30` 은 2자, `\frac{1}{2}` 은 2자). 서버는 수식을 1자로 세므로 이 값은 항상
 * 서버보다 크거나 같다 — 공백뿐인 수식만 양쪽 모두 0자다.
 */
export const countFormulaSymbols = (latex: string): number => {
  const source = latex.replace(ZWSP_PATTERN, '');
  if (BLANK_PATTERN.test(source)) return 0;

  const cached = cache.get(source);
  if (cached !== undefined) return cached;

  const symbols = countRenderedSymbols(source);
  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(source, symbols);
  return symbols;
};
